import useUserInteractionTracker from '../components/Hooks/useUserInteractionTracker';

/**
 * Utility functions for tracking user interactions across the application
 * This file provides standardized tracking functions for various components and interactions
 * 
 * The tracking system has been refactored to use DenoKV storage instead of Supabase
 * with improved offline support, error handling, and rate limiting
 */

// Create a singleton instance of the tracker
let trackInteractionFunc = null;
let clearSessionDataFunc = null;
let clearAllPendingDataFunc = null;
let pendingCountRef = 0;
let isOnlineRef = true;
let isServerAvailableRef = true;

/**
 * Custom hook for tracking interactions
 * This is a wrapper around useUserInteractionTracker that provides a simpler interface
 * @returns {Object} - Functions for tracking interactions
 */
export const useTrackInteractions = () => {
  const {
    trackInteraction,
    saveSessionData,
    clearSessionData,
    clearAllPendingData,
    trackingEnabled,
    setTrackingEnabled,
    pendingCount,
    isOnline,
    isServerAvailable
  } = useUserInteractionTracker();

  // Initialize the singleton instance if not already initialized
  if (!trackInteractionFunc) {
    initializeTracker(trackInteraction, clearSessionData, clearAllPendingData);
  }
  
  // Update the refs with the latest values
  pendingCountRef = pendingCount;
  isOnlineRef = isOnline;
  isServerAvailableRef = isServerAvailable;

  return {
    // Core functions
    trackInteraction,
    saveSessionData,
    clearSessionData,
    clearAllPendingData,
    trackingEnabled,
    setTrackingEnabled,
    pendingCount,
    isOnline,
    isServerAvailable,
    
    // Convenience tracking functions
    trackPageView: (pageName, additionalData) => trackPageView(pageName, additionalData),
    trackComponentView: (componentName, additionalData) => trackComponentView(componentName, additionalData),
    trackButtonClick: (buttonName, componentName, additionalData) => trackButtonClick(buttonName, componentName, additionalData),
    trackMenuClick: (menuItem, menuName, additionalData) => trackMenuClick(menuItem, menuName, additionalData),
    trackFormSubmit: (formName, formData, additionalData) => trackFormSubmit(formName, formData, additionalData),
    trackMapInteraction: (interactionType, mapData, additionalData) => trackMapInteraction(interactionType, mapData, additionalData),
    trackMapLayerToggle: (layerName, isVisible, additionalData) => trackMapLayerToggle(layerName, isVisible, additionalData),
    trackTodoAction: (actionType, todoId, additionalData) => trackTodoAction(actionType, todoId, additionalData),
    trackTimeTrackAction: (actionType, additionalData) => trackTimeTrackAction(actionType, additionalData),
    trackTimeTrackAutoBreakEnd: (breakType, duration, additionalData) => trackTimeTrackAutoBreakEnd(breakType, duration, additionalData)
  };
};

/**
 * Initialize the interaction tracker
 * This should be called in a component that has access to the StoreContext
 * @param {Function} trackInteraction - The trackInteraction function from useUserInteractionTracker
 * @param {Function} clearSessionData - The clearSessionData function from useUserInteractionTracker
 * @param {Function} clearAllPendingData - The clearAllPendingData function from useUserInteractionTracker
 */
export const initializeTracker = (trackInteraction, clearSessionData, clearAllPendingData) => {
  trackInteractionFunc = trackInteraction;
  clearSessionDataFunc = clearSessionData;
  clearAllPendingDataFunc = clearAllPendingData;
};

/**
 * Check if the tracker is initialized
 * @returns {boolean} - Whether the tracker is initialized
 */
export const isTrackerInitialized = () => {
  return !!trackInteractionFunc;
};

/**
 * Get the number of pending interactions
 * @returns {number} - Number of pending interactions
 */
export const getPendingCount = () => {
  return pendingCountRef;
};

/**
 * Check if the device is online
 * @returns {boolean} - Whether the device is online
 */
export const isOnline = () => {
  return isOnlineRef;
};

/**
 * Check if the server is available
 * @returns {boolean} - Whether the server is available
 */
export const isServerAvailable = () => {
  return isServerAvailableRef;
};

/**
 * Get the current timestamp
 * @returns {number} - Current timestamp in milliseconds
 */
const getTimestamp = () => Date.now();

/**
 * Get the current URL
 * @returns {string} - Current URL
 */
const getCurrentUrl = () => window.location.href;

/**
 * Track a page view
 * @param {string} pageName - Name of the page being viewed
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackPageView = (pageName, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('page_view', {
    pageName,
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a component view
 * @param {string} componentName - Name of the component being viewed
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackComponentView = (componentName, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('component_view', {
    componentName,
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a button click
 * @param {string} buttonName - Name of the button being clicked
 * @param {string} componentName - Name of the component containing the button
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackButtonClick = (buttonName, componentName, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('button_click', {
    buttonName,
    componentName,
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a menu click
 * @param {string} menuItem - Name of the menu item being clicked
 * @param {string} menuName - Name of the menu
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackMenuClick = (menuItem, menuName, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('menu_click', {
    menuItem,
    menuName,
    componentName: menuName, // Add componentName for tracking config
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a form submission
 * @param {string} formName - Name of the form being submitted
 * @param {Object} formData - Data being submitted (sensitive data should be excluded)
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackFormSubmit = (formName, formData = {}, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  // Remove any sensitive fields from formData
  const sanitizedFormData = { ...formData };
  const sensitiveFields = ['password', 'token', 'secret', 'credit', 'card', 'ssn', 'social'];
  
  Object.keys(sanitizedFormData).forEach(key => {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
      sanitizedFormData[key] = '[REDACTED]';
    }
  });

  trackInteractionFunc('form_submit', {
    formName,
    formData: sanitizedFormData,
    componentName: additionalData.componentName || formName,
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a map interaction
 * @param {string} interactionType - Type of map interaction (click, move, zoom, etc.)
 * @param {Object} mapData - Data about the map interaction
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackMapInteraction = (interactionType, mapData = {}, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('map_interaction', {
    interactionType,
    mapData,
    componentName: 'Map',
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a map layer toggle
 * @param {string} layerName - Name of the layer being toggled
 * @param {boolean} isVisible - Whether the layer is now visible
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackMapLayerToggle = (layerName, isVisible, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('map_layer_toggle', {
    layerName,
    isVisible,
    componentName: 'Map',
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a todo action
 * @param {string} actionType - Type of todo action (add, complete, delete, etc.)
 * @param {string} todoId - ID of the todo
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackTodoAction = (actionType, todoId, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('todo_action', {
    actionType,
    todoId,
    componentName: 'TodoList',
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track a time tracking action
 * @param {string} actionType - Type of time tracking action (clock_in, clock_out, break_start, break_end, etc.)
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackTimeTrackAction = (actionType, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('time_track_action', {
    actionType,
    componentName: 'TimeTrack',
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    ...additionalData
  });
};

/**
 * Track an automatic break end in time tracking
 * @param {string} breakType - Type of break (meal, rest, etc.)
 * @param {number} duration - Duration of the break in milliseconds
 * @param {Object} additionalData - Additional data to include with the interaction
 */
export const trackTimeTrackAutoBreakEnd = (breakType, duration, additionalData = {}) => {
  if (!trackInteractionFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  trackInteractionFunc('time_track_auto_break_end', {
    breakType,
    duration,
    componentName: 'TimeTrack',
    url: getCurrentUrl(),
    timestamp: getTimestamp(),
    automatic: true,
    ...additionalData
  });
};

/**
 * Clear all session data
 * This should be called when the user logs out
 */
export const clearAllSessionData = () => {
  if (!clearSessionDataFunc) {
    console.warn('Interaction tracker not initialized');
    return;
  }

  clearSessionDataFunc();
};

export default {
  initializeTracker,
  trackPageView,
  trackComponentView,
  trackButtonClick,
  trackMenuClick,
  trackFormSubmit,
  trackMapInteraction,
  trackMapLayerToggle,
  trackTodoAction,
  trackTimeTrackAction,
  trackTimeTrackAutoBreakEnd,
  clearAllSessionData
};
